import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MaterialTable, { MaterialTableProps } from 'material-table';
import Moment from 'moment';
import { toast } from 'react-toastify';
import DocumentTypeEntity from '../../../entities/DocumentType.Entity';
import ReferralDocumentEntity from '../../../entities/ReferralDocument.Entity';
import ServiceTypeEntity from '../../../entities/ServiceType.Entity';
import StateEntity from '../../../entities/State.Entity';
import AppointmentTypeEntity from '../../../entities/AppointmentType.Entity';
import DestinationEntity from '../../../entities/Destination.Entity';
import InterpretationTypeEntity from '../../../entities/InterpretationType.Entity';
import OriginationEntity from '../../../entities/Origination.Entity';
import TransportationRequiredEntity from '../../../entities/TransportationRequired.Entity';
import LanguageEntity from '../../../entities/Language.Entity';
import TransportationDataModel from '../../../data-models/Transportation.DataModel';
import TranslationDataModel from '../../../data-models/Translation.DataModel';
import { WheelchairOrStrecherDetailEntity } from '../../../entities/WheelchairOrStrecherDetail.Entity';
import { TtReferralForm } from '../../referral-components/referral-add/referral-form/ms-referral-form/TtReferralForm.Component';
import {
    getTtValidationToastMessage,
    isTtFormValid,
    validateTtFields,
} from '../../referral-components/referral-add/referral-service/TtFieldValidation';
import { translationTypes } from '../../../support/constants/TranslationTypes.Constants';
import attachStyles from '../attach-documents-submit-modal/AttachDocumentsSubmitModal.Styles';
import ServiceTypeRequestTransportationEntity from '../../../entities/ServiceTypeRequestTransportation.Entity';
import ServiceTypeRequestTranslationEntity from '../../../entities/ServiceTypeRequestTranslation.Entity';

function createEmptyTransportationDataModel(): TransportationDataModel {
    const model = new TransportationDataModel();
    model.serviceTypeRequestTransportation = new ServiceTypeRequestTransportationEntity();
    model.serviceTypeRequestTransportation.destinations = [];
    return model;
}

function createEmptyTranslationDataModel(): TranslationDataModel {
    const model = new TranslationDataModel();
    model.serviceTypeRequestTranslation = new ServiceTypeRequestTranslationEntity();
    return model;
}

export interface TtSubmitReferralPayload {
    documents: Array<ReferralDocumentEntity>;
}

interface ITtReferralSubmitModalProps {
    open: boolean;
    loading: boolean;
    documentTypes: Array<DocumentTypeEntity>;
    availableDocuments: Array<ReferralDocumentEntity>;
    onClose: () => void;
    onLoadFormData: () => void;
    onSearchDocuments: (
        selectedDocumentTypeIds: Array<number>,
        dateFrom: Date | null,
        dateTo: Date | null,
    ) => void;
    onSubmitWithDocuments: (payload: TtSubmitReferralPayload) => void;
    onValidationFailed: () => void;
    referralId: number;
    states: Array<StateEntity>;
    serviceTypes: Array<ServiceTypeEntity>;
    translationService: TranslationDataModel | null;
    transportationService: TransportationDataModel | null;
    onTranslationServiceChange: (edit: TranslationDataModel) => void;
    onTransportationServiceChange: (edit: TransportationDataModel) => void;
    appointmentTypes: Array<AppointmentTypeEntity>;
    interpretationTypes: Array<InterpretationTypeEntity>;
    languages: Array<LanguageEntity>;
    transportationRequireds: Array<TransportationRequiredEntity>;
    originations: Array<OriginationEntity>;
    destinations: Array<DestinationEntity>;
    wheelchairOrStrecherDetail: Array<WheelchairOrStrecherDetailEntity>;
    showTtValidationError: boolean;
}

const TableContainer: React.FunctionComponent = ({ children }) => <>{children}</>;

const noopSetValidation = (_fn: () => boolean) => undefined;
const noopSetIsValidTt = (_fn: () => boolean, _serviceTypeString: string) => undefined;

export const TtReferralSubmitModal: FunctionComponent<ITtReferralSubmitModalProps> = (
    props: ITtReferralSubmitModalProps,
) => {
    const { onLoadFormData, open } = props;
    const classes = attachStyles();

    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<Array<number>>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<Array<ReferralDocumentEntity>>([]);
    const [dateRangeFrom, setDateRangeFrom] = useState('');
    const [dateRangeTo, setDateRangeTo] = useState('');
    const directOpenLoadRequestedRef = useRef(false);

    const showTransportation = props.serviceTypes.some((s) => s.serviceTypeAbbreviation === 'TP');
    const showTranslation = props.serviceTypes.some((s) => translationTypes.includes(s.serviceTypeAbbreviation));

    const rushRequest = useMemo(() => {
        const translation = props.translationService?.serviceTypeRequestTranslation;
        const transportation = props.transportationService?.serviceTypeRequestTransportation;
        return !!(translation?.rushRequest || transportation?.rushRequest);
    }, [props.translationService, props.transportationService]);

    const handleRushRequestChange = (checked: boolean) => {
        if (showTransportation && props.transportationService) {
            const model = { ...props.transportationService };
            const entity = model.serviceTypeRequestTransportation
                ? { ...model.serviceTypeRequestTransportation }
                : new ServiceTypeRequestTransportationEntity();
            entity.rushRequest = checked;
            model.serviceTypeRequestTransportation = entity;
            props.onTransportationServiceChange(model);
        }
        if (showTranslation && props.translationService) {
            const model = { ...props.translationService };
            const entity = model.serviceTypeRequestTranslation
                ? { ...model.serviceTypeRequestTranslation }
                : new ServiceTypeRequestTranslationEntity();
            entity.rushRequest = checked;
            model.serviceTypeRequestTranslation = entity;
            props.onTranslationServiceChange(model);
        }
    };

    useEffect(() => {
        if (!open) {
            setSelectedDocumentTypeIds([]);
            setSelectedDocuments([]);
            setDateRangeFrom('');
            setDateRangeTo('');
            directOpenLoadRequestedRef.current = false;
            return;
        }
        if (!directOpenLoadRequestedRef.current) {
            directOpenLoadRequestedRef.current = true;
            onLoadFormData();
        }
    }, [open, onLoadFormData]);

    const orderedDocumentTypes = useMemo(
        () =>
            [...(props.documentTypes ?? [])].sort(
                (a, b) => (a.orderToDisplay ?? Number.MAX_SAFE_INTEGER) - (b.orderToDisplay ?? Number.MAX_SAFE_INTEGER),
            ),
        [props.documentTypes],
    );

    const dateFilterAnchorDocumentTypeId = useMemo(() => {
        const selectedOfGroup = orderedDocumentTypes.filter(
            (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
        );
        if (selectedOfGroup.length === 0) {
            return null;
        }
        return selectedOfGroup[selectedOfGroup.length - 1].documentTypeId;
    }, [orderedDocumentTypes, selectedDocumentTypeIds]);

    const selectionRequiresDateFilter = useMemo(
        () =>
            orderedDocumentTypes.some(
                (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
            ),
        [orderedDocumentTypes, selectedDocumentTypeIds],
    );

    useEffect(() => {
        if (dateFilterAnchorDocumentTypeId == null) {
            setDateRangeFrom('');
            setDateRangeTo('');
        }
    }, [dateFilterAnchorDocumentTypeId]);

    const selectedDocumentNumberSet = useMemo(
        () => new Set((selectedDocuments ?? []).map((d) => d.documentNumber)),
        [selectedDocuments],
    );

    const tableData = useMemo(
        () =>
            (props.availableDocuments ?? []).map((doc) => ({
                ...doc,
                tableData: {
                    ...(doc as { tableData?: { checked?: boolean } }).tableData,
                    checked: selectedDocumentNumberSet.has(doc.documentNumber),
                },
            })),
        [props.availableDocuments, selectedDocumentNumberSet],
    );

    const handleClose = () => {
        setSelectedDocumentTypeIds([]);
        setSelectedDocuments([]);
        props.onClose();
    };

    const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const allIds = checked ? orderedDocumentTypes.map((type) => type.documentTypeId) : [];
        setSelectedDocumentTypeIds(allIds);
        if (!checked) {
            setSelectedDocuments([]);
        }
    };

    const handleSelectDocumentType = (documentTypeId: number, checked: boolean) => {
        const updatedIds = checked
            ? [...selectedDocumentTypeIds, documentTypeId]
            : selectedDocumentTypeIds.filter((id) => id !== documentTypeId);

        setSelectedDocumentTypeIds(updatedIds);

        if (updatedIds.length === 0) {
            setSelectedDocuments([]);
            return;
        }

        setSelectedDocuments((previousSelected) =>
            previousSelected.filter((document) => updatedIds.includes(document.documentTypeId)),
        );
    };

    const handleSearchDocuments = () => {
        let dateFrom: Date | null = null;
        let dateTo: Date | null = null;
        if (selectionRequiresDateFilter) {
            if (dateRangeFrom) {
                dateFrom = new Date(`${dateRangeFrom}T00:00:00`);
            }
            if (dateRangeTo) {
                dateTo = new Date(`${dateRangeTo}T00:00:00`);
            }
        }
        props.onSearchDocuments(selectedDocumentTypeIds, dateFrom, dateTo);
    };

    const handleSubmit = () => {
        const translation = props.translationService?.serviceTypeRequestTranslation;
        const transportation = props.transportationService?.serviceTypeRequestTransportation;

        const ttErrors = validateTtFields({
            showTranslation,
            showTransportation,
            translation: showTranslation
                ? {
                      appointmentTypeId: translation?.appointmentTypeId,
                      otherAppointmentType: translation?.otherAppointmentType,
                      appointmentDate: translation?.appointmentDate,
                      frequencyAndDuration: translation?.frequencyAndDuration,
                      languageId: translation?.languageId,
                      otherLanguage: translation?.otherLanguage,
                  }
                : undefined,
            transportation: showTransportation
                ? {
                      transportationRequiredId: transportation?.transportationRequiredId,
                      otherTransportationRequired: transportation?.otherTransportationRequired,
                      appointmentDate: transportation?.appointmentDate,
                      frequencyAndDuration: transportation?.frequencyAndDuration,
                      originationId: transportation?.originationId,
                      originationContactNumber: transportation?.originationContactNumber,
                      destinationId: transportation?.destinations?.[0]?.destinationId,
                  }
                : undefined,
        });

        if (!isTtFormValid(ttErrors)) {
            props.onValidationFailed();
            const toastMessage = getTtValidationToastMessage(ttErrors);
            if (toastMessage) {
                toast.warning(`⚠️ ${toastMessage}`);
            }
            return;
        }

        props.onSubmitWithDocuments({
            documents: selectedDocuments,
        });
    };

    const renderDateRangeFields = (currentDocumentTypeId: number) => {
        if (currentDocumentTypeId !== dateFilterAnchorDocumentTypeId) {
            return null;
        }

        return (
            <div className={classes.dateRangeBlock}>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="tt-attach-docs-date-from" className={classes.dateRangeFieldLabel}>
                        Date Range From
                    </Typography>
                    <TextField
                        id="tt-attach-docs-date-from"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeFrom}
                        onChange={(e) => setDateRangeFrom(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="tt-attach-docs-date-to" className={classes.dateRangeFieldLabel}>
                        To
                    </Typography>
                    <TextField
                        id="tt-attach-docs-date-to"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeTo}
                        onChange={(e) => setDateRangeTo(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
            </div>
        );
    };

    const tableProps: MaterialTableProps<ReferralDocumentEntity> = {
        columns: [
            { title: 'Description', field: 'fileDescription' },
            { title: 'File Name', field: 'fileName' },
            { title: 'Document Type', field: 'documentType' },
            {
                title: 'Date',
                render: (rowData) => (rowData.dueDate ? Moment(rowData.dueDate).format('M/D/YYYY') : ''),
            },
        ],
        data: tableData,
        options: {
            selection: true,
            paging: false,
            toolbar: false,
            maxBodyHeight: 220,
            minBodyHeight: 220,
            headerStyle: {
                paddingTop: 0,
                paddingBottom: 0,
                height: 16,
                minHeight: 16,
                fontSize: '13px',
                lineHeight: '14px',
            },
            selectionProps: (rowData: ReferralDocumentEntity) => ({
                disabled: !selectedDocumentTypeIds.includes(rowData.documentTypeId),
                color: 'secondary',
            }),
            rowStyle: (rowData: ReferralDocumentEntity) =>
                selectedDocumentTypeIds.includes(rowData.documentTypeId) ? undefined : { opacity: 0.6 },
        },
        onSelectionChange: (rows) =>
            setSelectedDocuments(
                (rows ?? []).filter((d) => selectedDocumentTypeIds.includes(d.documentTypeId)),
            ),
    };

    const isAllSelected =
        orderedDocumentTypes.length > 0 && selectedDocumentTypeIds.length === orderedDocumentTypes.length;

    const formReady =
        props.serviceTypes.length > 0 &&
        (!showTranslation || props.translationService != null) &&
        (!showTransportation || props.transportationService != null);

    const catalogsReady =
        (!showTranslation || (props.appointmentTypes?.length > 0 && props.languages?.length > 0)) &&
        (!showTransportation ||
            (props.transportationRequireds?.length > 0 &&
                props.originations?.length > 0 &&
                props.destinations?.length > 0));

    const documentsCatalogReady = orderedDocumentTypes.length > 0 || !props.loading;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ style: { borderRadius: 4 } }}
        >
            <DialogTitle className={classes.siuExpandedTitle} disableTypography>
                <Typography component="span" variant="h6" className={classes.siuExpandedTitleText}>
                    Additional Information and Documents
                </Typography>
                <IconButton aria-label="close" className={classes.siuExpandedCloseButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.expandedDialogContent}>
                {(showTransportation || showTranslation) && (
                    <FormControlLabel
                        className={classes.rushRow}
                        control={
                            <Checkbox
                                color="secondary"
                                checked={rushRequest}
                                onChange={(_e, checked) => handleRushRequestChange(checked)}
                                disabled={!formReady}
                            />
                        }
                        label="This is a Rush Request"
                    />
                )}

                {!formReady ? (
                    <div className={classes.tableContainer}>
                        <CircularProgress size={24} />
                    </div>
                ) : (
                    <Grid container spacing={2}>
                        <TtReferralForm
                            referralId={props.referralId}
                            states={props.states ?? []}
                            handleSetValidationFunction={noopSetValidation}
                            handleSetIsValidTtFunction={noopSetIsValidTt}
                            serviceTypes={props.serviceTypes}
                            nextStep={false}
                            handleTranslationServiceEdit={props.onTranslationServiceChange}
                            translationService={props.translationService ?? createEmptyTranslationDataModel()}
                            appointmentTypes={props.appointmentTypes ?? []}
                            interpretationTypes={props.interpretationTypes ?? []}
                            languages={props.languages ?? []}
                            handleTransportationServiceEdit={props.onTransportationServiceChange}
                            transportationService={props.transportationService ?? createEmptyTransportationDataModel()}
                            transportationRequireds={props.transportationRequireds ?? []}
                            originations={props.originations ?? []}
                            destinations={props.destinations ?? []}
                            wheelchairOrStrecherDetail={props.wheelchairOrStrecherDetail ?? []}
                            showTtValidationError={props.showTtValidationError}
                            suppressRushAndComments
                        />
                    </Grid>
                )}

                <div className={classes.documentsSectionSpacedTop}>
                <div className={classes.documentsSectionHeader}>
                            <Typography variant="subtitle1" className={classes.documentsSectionTitle}>
                                Documents
                            </Typography>
                        </div>

                        <FormControlLabel
                            className={`${classes.filterLabel} ${classes.selectAllLabel}`}
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                    disabled={props.loading || orderedDocumentTypes.length === 0}
                                />
                            }
                            label="Select All"
                        />

                        <div className={classes.documentTypesColumnFlow}>
                            {orderedDocumentTypes.map((documentType) => (
                                <div key={documentType.documentTypeId} className={classes.documentTypesColumnItem}>
                                    <FormControlLabel
                                        className={`${classes.filterLabel} ${classes.documentTypeLabel}`}
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={selectedDocumentTypeIds.includes(documentType.documentTypeId)}
                                                onChange={(_event, checked) =>
                                                    handleSelectDocumentType(documentType.documentTypeId, checked)
                                                }
                                                disabled={props.loading}
                                            />
                                        }
                                        label={documentType.documentTypeName}
                                    />
                                    {renderDateRangeFields(documentType.documentTypeId)}
                                </div>
                            ))}
                        </div>

                        <div className={classes.searchDocumentsRow}>
                            <Button
                                variant="outlined"
                                className={`${classes.secondaryButton} ${classes.searchDocumentsButton}`}
                                onClick={handleSearchDocuments}
                                disabled={
                                    props.loading ||
                                    orderedDocumentTypes.length === 0 ||
                                    selectedDocumentTypeIds.length === 0
                                }
                            >
                                Search Documents
                            </Button>
                        </div>

                        <Typography variant="subtitle2" className={classes.tableTitle}>
                            Available Documents
                        </Typography>

                <div className={classes.tableContainer}>
                    {!documentsCatalogReady ? (
                        <CircularProgress size={24} />
                    ) : (
                        <MaterialTable
                            components={{
                                Container: TableContainer,
                            }}
                            {...tableProps}
                        />
                    )}
                </div>
                </div>
            </DialogContent>

            <DialogActions className={classes.footerActionsBar}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    className={classes.secondaryButton}
                >
                    Back
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    className={classes.primaryButton}
                    disabled={props.loading || !formReady || !catalogsReady}
                >
                    Submit Referral
                </Button>
            </DialogActions>
        </Dialog>
    );
};
